import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Box, Button, Chip, Stack, Tab, Tabs, Typography } from '@mui/material';
import { SignaturePad, SignaturePadHandle } from '../../components/SignaturePad';
import { SignatureMethod, StudentSignatureMeta } from '../../api/studentSignature';

const MAX_UPLOAD_BYTES = 2 * 1024 * 1024;

export interface StudentSignatureAdapter {
  queryKey: unknown[];
  getSignature: () => Promise<StudentSignatureMeta | null>;
  getSignatureImage: () => Promise<{ blob: Blob; mimeType: string }>;
  saveSignature: (method: SignatureMethod, file: Blob) => Promise<StudentSignatureMeta>;
}

/** "Student Signature" section (Phase 78) — Digi Pad (draw, mouse/touch)
 * or Upload (PNG/JPG), with a preview and Clear/Replace/Save actions,
 * reused identically on the Application Wizard's Declaration step and
 * the Student detail page (parameterized by `adapter` so both the
 * staff-facing and Student Portal self-service API call sets can drive
 * the exact same UI without duplicating it). */
export const StudentSignatureSection = ({ adapter }: { adapter: StudentSignatureAdapter }) => {
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<SignatureMethod>('DRAWN');
  const [draftFile, setDraftFile] = useState<Blob | null>(null);
  const [draftPreviewUrl, setDraftPreviewUrl] = useState<string | null>(null);
  const [savedPreviewUrl, setSavedPreviewUrl] = useState<string | null>(null);
  const [replacing, setReplacing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const padRef = useRef<SignaturePadHandle>(null);

  const metaQuery = useQuery({ queryKey: adapter.queryKey, queryFn: adapter.getSignature });
  // Replacing a signature upserts the same row (same id), so `updatedAt` —
  // not `id` — is what actually changes on every save and must drive the
  // image refetch, or the preview keeps showing the pre-replace blob.
  const signatureVersion = metaQuery.data?.updatedAt;

  useEffect(() => {
    if (!signatureVersion) {
      setSavedPreviewUrl(null);
      return;
    }
    let url: string | null = null;
    adapter.getSignatureImage().then(({ blob }) => {
      url = URL.createObjectURL(blob);
      setSavedPreviewUrl(url);
    });
    return () => {
      if (url) URL.revokeObjectURL(url);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signatureVersion]);

  const clearDraft = () => {
    padRef.current?.clear();
    if (draftPreviewUrl) URL.revokeObjectURL(draftPreviewUrl);
    setDraftFile(null);
    setDraftPreviewUrl(null);
    setError(null);
  };

  const handleUploadFile = (file: File) => {
    if (!['image/png', 'image/jpeg'].includes(file.type)) {
      setError('Only PNG or JPG images are allowed.');
      return;
    }
    if (file.size > MAX_UPLOAD_BYTES) {
      setError('Signature image must be under 2MB.');
      return;
    }
    setError(null);
    setDraftFile(file);
    setDraftPreviewUrl(URL.createObjectURL(file));
  };

  const saveMutation = useMutation({
    mutationFn: async () => {
      let file = draftFile;
      if (tab === 'DRAWN' && !file) {
        if (padRef.current?.isEmpty()) throw new Error('empty');
        file = await padRef.current!.toBlob();
      }
      if (!file) throw new Error('empty');
      return adapter.saveSignature(tab, file);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adapter.queryKey });
      setError(null);
      setSuccess(true);
      setReplacing(false);
      clearDraft();
    },
    onError: () => setError('Could not save the signature. Please try again.'),
  });

  const hasSaved = !!metaQuery.data;
  const showCapture = !hasSaved || replacing;

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Student Signature
      </Typography>
      {error && (
        <Alert severity="error" sx={{ mb: 1 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 1 }}>
          Signature saved.
        </Alert>
      )}

      {!showCapture && (
        <Stack spacing={1} alignItems="flex-start">
          <Chip size="small" label={metaQuery.data!.method === 'DRAWN' ? 'Drawn signature' : 'Uploaded signature'} />
          {savedPreviewUrl && (
            <Box
              component="img"
              src={savedPreviewUrl}
              alt="Saved signature"
              sx={{ maxWidth: 340, maxHeight: 120, border: '1px solid', borderColor: 'divider', borderRadius: 1, bgcolor: '#fff' }}
            />
          )}
          <Button size="small" onClick={() => setReplacing(true)}>
            Replace signature
          </Button>
        </Stack>
      )}

      {showCapture && (
        <Stack spacing={1.5} alignItems="flex-start">
          <Tabs
            value={tab}
            onChange={(_e, v: SignatureMethod) => {
              setTab(v);
              clearDraft();
            }}
          >
            <Tab label="Digi Pad" value="DRAWN" />
            <Tab label="Upload" value="UPLOADED" />
          </Tabs>

          {tab === 'DRAWN' ? (
            <SignaturePad ref={padRef} />
          ) : draftPreviewUrl ? (
            <Box
              component="img"
              src={draftPreviewUrl}
              alt="Signature preview"
              sx={{ maxWidth: 340, maxHeight: 120, border: '1px solid', borderColor: 'divider', borderRadius: 1, bgcolor: '#fff' }}
            />
          ) : (
            <Button variant="outlined" component="label">
              Choose image
              <input
                type="file"
                accept="image/png,image/jpeg"
                hidden
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) handleUploadFile(file);
                }}
              />
            </Button>
          )}

          <Stack direction="row" spacing={1}>
            <Button size="small" onClick={clearDraft}>
              Clear
            </Button>
            {replacing && (
              <Button
                size="small"
                onClick={() => {
                  setReplacing(false);
                  clearDraft();
                }}
              >
                Cancel
              </Button>
            )}
            <Button size="small" variant="contained" disabled={saveMutation.isPending} onClick={() => saveMutation.mutate()}>
              {saveMutation.isPending ? 'Saving…' : 'Save'}
            </Button>
          </Stack>
        </Stack>
      )}
    </Box>
  );
};
